import iti from 'intl-tel-input';
import isEmpty from 'lodash/isEmpty';
import getValue from 'lodash/get';
import IMask from 'imask/esm/imask';
import { functionalConsentChange$ } from 'commons/one_trust/consent';
import { escapeHtmlContent } from 'commons/utils/sanitize';
import validations from 'commons/utils/validations';
import { formatDate } from 'commons/utils/date';
import { ContactSiteApi } from '../annotations/api';
import TrialSite from './TrialSite';

function mapTrialSite({ site, trialId, distanceUnit }) {
  const {
    site_id: id,
    site_name: name,
    site_email: email,
    site_phone: phone,
    site_address: address,
    site_distance: distance,
    site_location: location,
    site_eligibility: eligibility,
    site_status: status,
    site_icon: siteIcon,
    investigator_name: investigatorName,
    investigator_avatar: investigatorAvatar,
  } = site;

  const trialSite = new TrialSite({
    id,
    trialId,
    name,
    email,
    address,
    phone,
    distance,
    location,
    eligibility,
    status,
    distanceUnit,
    siteIcon,
    investigatorName,
    investigatorAvatar,
  });

  return trialSite;
}

function handleSiteConnect({ trialId, site, distanceUnit }, event) {
  event.preventDefault();
  this.resetContactedSite();

  const trialSite = mapTrialSite({ site, trialId, distanceUnit });
  this.updateContactSiteForm(trialSite);
}

function updateContactSiteForm(trialSite) {
  this.resetContactForm();
  this.trialSite(trialSite);
  this.showContactSiteTab();
}

function handleTrialSitesChange({ sites = [], trialId, distanceUnit }) {
  const sitesFetched = this.isSitesFetched();
  // Show message tab incase of single annotation on initial load
  if (!sitesFetched && Array.isArray(sites) && sites.length === 1) {
    const trialSite = mapTrialSite({ site: sites[0], trialId, distanceUnit });
    this.updateContactSiteForm(trialSite);
  }

  this.isSitesFetched(true);
}

function getDialingCode() {
  return this.$phoneInputIntl.getSelectedCountryData().dialCode;
}

function resetContactForm() {
  this.patientName(this.int_name);
  this.patientEmail(this.int_email);
  this.patientPhone(this.int_phone);
  this.patientMessage("");
  this.isSubmitted(false);
  this.isEmailValidatedError(false);
  this.captchaError(null);
  this.contactSiteError(null);
  this.phoneEmailText(null);
  this.acceptedPrivacyTerms(false);
  this.preferredLanguage(null);
  this.otherLanguage(null);
  this.is_otherLanguage(false);
  this.countryDialCode(this.getDialingCode());
  window.grecaptcha && window.grecaptcha.reset && window.grecaptcha.reset();
}

function showContactSiteTab() {
  if (this.$contactSiteTab) {
    this.enableContactSiteTab();
    this.$contactSiteTab.tab('show');
  }
}

function enableContactSiteTab() {
  if (this.$contactSiteTab) {
    this.$contactSiteTab.removeClass('disabled');
    this.$contactSiteTab.attr('aria-disabled', false);
  }
}

function isContactSiteTabDisabled() {
  if (this.$contactSiteTab) {
    return this.$contactSiteTab.hasClass('disabled');
  }

  return false;
}

function validateContactForm() {
  const errors = {};
  const patientName = this.patientName();
  const patientEmail = this.patientEmail();
  const patientPhoneMasked = this.patientPhone(); // For errors computed property binding
  const patientPhoneUnmasked = this.phoneMask.unmaskedValue;
  const acceptedPrivacyTerms = this.acceptedPrivacyTerms();
  const preferredLanguage = this.preferredLanguage();
  const otherLanguage = this.otherLanguage();
  const captchaResponse = $('#g-recaptcha-response').val();
  const countryDialCode = this.countryDialCode();

  const nameError = validations.required(
    patientName,
    getValue(this.referralFormLocale, 'nameInputErrorRequired'),
  );
  const emailError =
    validations.required(
      patientEmail,
      getValue(this.referralFormLocale, 'emailInputErrorRequired'),
    ) ||
    validations.email(patientEmail, {
      message: getValue(this.referralFormLocale, 'emailInputErrorInvalid'),
    });
  const phoneError =
    validations.required(
      countryDialCode,
      getValue(this.referralFormLocale, 'phoneCountryCodeErrorRequired'),
    ) ||
    validations.required(
      patientPhoneUnmasked,
      getValue(this.referralFormLocale, 'phoneInputErrorRequired'),
    ) ||
    validations.phoneNumber(patientPhoneUnmasked, {
      message: getValue(this.referralFormLocale, 'phoneInputErrorInvalid'),
      minDigits: 5,
      maxDigits: 15,
    });
  const captchaError = validations.required(
    captchaResponse,
    getValue(this.referralFormLocale, 'recaptchaError'),
  );
  //  const preferredLangError = validations.required(preferredLanguage);
  const otherLangError = validations.required(
    otherLanguage,
    getValue(this.referralFormLocale, 'languageInputErrorRequired'),
  );
  if (nameError) errors.name = nameError;
  if (emailError) errors.email = emailError;
  if (phoneError) errors.phone = phoneError;
  if (captchaError) errors.captcha = captchaError;
  //  if (preferredLangError) errors.preferred = preferredLangError;
  if (this.is_otherLanguage())
    errors.other = this.is_otherLanguage()
      ? otherLangError
      : delete errors['other'];
  if (otherLanguage) delete errors['other'];
  if (!acceptedPrivacyTerms)
    errors.privacyTerms = getValue(
      this.referralFormLocale,
      'privacyConsentError',
    );

  this.captchaError(captchaError);

  return isEmpty(errors) ? null : errors;
}

function TrialSiteHasRV(trial,site){
  const data = new FormData();
  data.append('trial_id',trial)
  data.append('site_id',site)
  return $.ajax({
          type:"POST",
          url:"/verification/hasrv/",
          data: data,
          processData: false,
          contentType: false,
  });
}

function phoneValidation(phoneComplete){
  return $.ajax({
          type:"POST",
          url:"/verification/phonecheck/",
          data: "phone=" + phoneComplete,
  });
}

async function checkIfTrialSiteHasRV(trial,site) {
  if (trial && site){
    try {
        const res = await TrialSiteHasRV(trial,site)
        return res
    } catch(err) {
        return 'NOK' // Any mapped BadRequest means: No RV for the Trial/Site
    }
  }else{
    return 'NOK' // No Trial/Site supplied
  }
}

async function checkPhoneRealWorldValidity(phone,dialcode) {
  if (phone && dialcode){
    phone = phone.toString();
    var stripped = phone.replace(/[\(\)\.\-\ ]/g, '');
    let phoneComplete = '+'+dialcode+''+stripped
    phoneComplete = encodeURIComponent(phoneComplete)
    try {
        const res = await phoneValidation(phoneComplete)
        return res
    } catch(err) {
        return 'NOK' // Any mapped BadRequest means: phone is not valid on real world verification (Twilio verify)
    }
  }else{
    return 'NOK' // No Phone or Dial Code supplied
  }
}


function handleContactSiteError(reason) {
  const errorReasons = {
    patient: getValue(this.referralFormLocale, 'contactSiteErrorPatient'),
    investigator: getValue(
      this.referralFormLocale,
      'contactSiteErrorInvestigator',
    ),
    recaptcha: getValue(this.referralFormLocale, 'contactSiteErrorCaptcha'),
  };
  const defaultErrorMessage = getValue(
    this.referralFormLocale,
    'contactSiteError',
  );
  const errorMessage = getValue(errorReasons, reason, defaultErrorMessage);

  this.contactSiteError(errorMessage);
}
function handleOtherLanguage(pref_lan) {
  var value = pref_lan.indexOf('Other');
  let disable_other = false;
  if (value >= 0) {
    disable_other = true;
  }
  this.is_otherLanguage(disable_other);
  this.preferredLanguage(pref_lan);
}
function dispatchEvent({
  organization,
  target,
  targetDetails: target_details,
  label,
}) {
  window.registerDynamicEvent &&
    window.registerDynamicEvent({
      organization,
      target,
      target_details,
      label,
      additional_detail1: window.location.href,
    });
}

function handleSitePhoneClick() {
  const trialSite = this.trialSite();
  this.phoneEmailText(trialSite && trialSite.phone);
}

function handleSiteEmailClick() {
  const trialSite = this.trialSite();
  this.phoneEmailText(trialSite && trialSite.email);
}

function setVolunteerLink({ trialId, existingVolunteer, volunteerLink }) {
  const widgetId = this.widgetId;

  if (!existingVolunteer) {
    const signUpVolunteerUrl =
      volunteerLink + `?tg=V&tgd=${widgetId || trialId}&label=DRSSV`;
    this.volunteerLink(signUpVolunteerUrl);
  }
}

function handleSignupVolunteer() {
  const contactedSite = this.contactedSite();
  const trialId = contactedSite && contactedSite.trialId;
  dispatchEvent({
    organization: 'iconnect',
    target: 'T',
    targetDetails: trialId,
    label: 'DRSCVS',
  });

  return true;
}

async function sendMessage({ includeAnnotations = false } = {}) {
  try {
    const hasUserConsent = this.hasUserConsent();
    const trialSite = this.trialSite();
    const { id: siteId, trialId } = trialSite;
    const widgetId = this.widgetId;
    this.isSubmitted(true);

    dispatchEvent({
      organization: 'iconnect',
      target: 'T',
      targetDetails: trialId,
      label: 'DRBC',
    });

    const name = escapeHtmlContent(this.patientName());
    const email = escapeHtmlContent(this.patientEmail());
    const phone = escapeHtmlContent(this.phoneMask.unmaskedValue);
    const dialingCode = escapeHtmlContent(this.countryDialCode());
    const message = escapeHtmlContent(this.patientMessage());
    const captchaResponse = $('#g-recaptcha-response').val();
    const preferredLanguage = escapeHtmlContent(this.preferredLanguage());
    const otherLanguage = escapeHtmlContent(this.otherLanguage());
    const country = $('#current_country_code').val();
    const language = $('#current_language_code').val();

    let invalid = "OK"
    let hasRV = "NOK"
    try{
      hasRV = await this.checkIfTrialSiteHasRV(trialId,siteId) // Checks for phone real world validity only if RV is enabled to the trial
    }catch{
      hasRV = "NOK"
    }
    if (hasRV === "OK"){ // If RV is enabled then tests real world validity of the phone number
      if (phone && dialingCode){
        invalid = await this.checkPhoneRealWorldValidity(phone,dialingCode);
        if (invalid === "OK") {
          this.isEmailValidatedError(false)
        }else{
          this.isEmailValidatedError(true)
        }
      }
    }

    let errors = this.validateContactForm();

    if (errors) { // Exit when errors are found in the form
      return;
    }

    if (invalid === "OK"){ // Exit when an invalid phone is found in the form

    }else{
      return;
    }

    this.isContactFormLoading(true);
    includeAnnotations
      ? this.isSendingMessageWithAnnotations(true)
      : this.isSendingMessage(true);
    this.contactSiteError(null);

    const { existingVolunteer, volunteerLink, rv_ptid, rv_site } =
      await ContactSiteApi.sendMessage({
        siteId,
        trialId,
        widgetId,
        name,
        email,
        phone,
        dialingCode,
        message,
        captchaResponse,
        includeAnnotations,
        hasUserConsent,
        preferredLanguage,
        otherLanguage,
        country,
        language,
      });

    this.contactedSite(trialSite);
    this.showRecipientEmailMessage(true);
    this.setVolunteerLink({ trialId, existingVolunteer, volunteerLink });
    this.isSiteContacted(true);

    $('#rv_ptid').val(rv_ptid)
    $('#rv_site').val(rv_site)
    $('#rv_source_site').val(siteId)
    $('#rv_trial').val(trialId)

    try {
      CheckSiteHasRV()
    }catch{
      console.log('No RV is active')
    }

    this.onSiteContacted &&
      this.onSiteContacted({
        trialId,
        site: trialSite,
        existingVolunteer,
        volunteerLink,
      });
    this.refreshWorkspace &&
      this.refreshWorkspace({ success: this.handleWorkspaceRefreshSuccess });
    this.resetContactForm();
    dispatchEvent({
      organization: 'iConnect',
      target: 'T',
      targetDetails: trialId,
      label: 'DRS',
    });
  } catch (error) {
    console.log(error.message);
    this.handleContactSiteError(error.message);
  } finally {
    this.isContactFormLoading(false);
    this.isSendingMessage(false);
    this.isSendingMessageWithAnnotations(false);
    window.grecaptcha && window.grecaptcha.reset && window.grecaptcha.reset();
  }
}

function sendMessageWithAnnotations() {
  this.sendMessage({ includeAnnotations: true });
}

function handleWorkspaceRefreshSuccess() {
  const $sitesContactedBtn = $('#sites-contacted-btn');
  const $sitesContactIcon = $sitesContactedBtn.find('.unread-badge');
  $sitesContactIcon.removeClass('d-none');

  $sitesContactedBtn.one('click', function () {
    $sitesContactIcon.addClass('d-none');
  });
}

function setTotalAnnotations(totalAnnotations) {
  this.totalAnnotations(totalAnnotations);
}

function getContactedSiteMessage() {
  const contactedSite = this.contactedSite();

  if (!contactedSite) return null;
  const siteName = contactedSite.site_name || contactedSite.name;
  const siteAddress = contactedSite.site_address || contactedSite.address;

  const contactedOn =
    contactedSite.timestamp || formatDate(new Date(), 'd MMMM yyyy');

  return { siteName, siteAddress, contactedOn };
}

function handleConsentChange(enabled) {
  this.hasUserConsent(enabled);
}

function resetContactedSite() {
  this.contactedSite(null);
  this.onResetSiteContacted && this.onResetSiteContacted();
}

function setRecaptchaSize() {
  const recaptchaBreakpoint = 430;
  this.$recaptchaRef.dataset.size = 'normal'
    // window.outerWidth < recaptchaBreakpoint ? 'compact' : 'normal';
}

function getDefaultUserLanguage() {
  const userLanguage = window.navigator.language || window.userLanguage || '';
  const countryRegex = /^.*?-(.*?)$/;
  const userCountry = getValue(userLanguage.match(countryRegex), '[1]', null);
  return userCountry ? userCountry.toLowerCase() : userCountry;
}

function handleCountryDialCodeChange() {
  const dialCode = this.getDialingCode();
  this.countryDialCode(dialCode);
}

function ContactSiteViewModel({
  tabId = null,
  onSiteContacted = null,
  refreshWorkspace = null,
  canSendWithAnnotations = false,
  contactedSite = null,
  hasWorkspace = false,
  widgetId = null,
  successMessages = [],
  referralFormLocale,
  onResetSiteContacted = null,
} = {}) {
  const self = this;
  const countryCode = $('#current_country_code').val() !== "global" ? $('#current_country_code').val() : "us" ;
  self.$contactSiteTab = tabId ? $(`#${tabId}`) : null;
  self.$recaptchaRef = document.getElementById('referral-form-recaptcha');
  self.$phoneInput = document.getElementById('phone-no');
  self.$phoneInputIntl = iti(self.$phoneInput, {
    separateDialCode: true,
    initialCountry: countryCode || getDefaultUserLanguage() || 'auto',
  });
  self.phoneMask = IMask(self.$phoneInput, {
    mask: '000000000000000',
  });
  self.onSiteContacted = onSiteContacted;
  self.refreshWorkspace = refreshWorkspace;
  self.canSendWithAnnotations = canSendWithAnnotations;
  self.alreadyContactedSite = !!contactedSite;
  self.widgetId = widgetId;
  self.referralFormLocale = referralFormLocale;

  self.trialSite = ko.observable();
  self.int_name = document.getElementsByName('name')[0].getAttribute('int-value');
  self.int_email = document.getElementsByName('email')[0].getAttribute('int-value');
  self.int_phone = document.getElementsByName('phone-no')[0].getAttribute('int-value');
  self.patientName = ko.observable(self.int_name);
  self.patientEmail = ko.observable(self.int_email);
  self.patientPhone = ko.observable(self.int_phone);
  self.patientMessage = ko.observable();
  self.isSubmitted = ko.observable(false);
  self.isEmailValidatedError = ko.observable(false);
  self.captchaError = ko.observable();
  self.isContactFormLoading = ko.observable(false);
  self.isSendingMessage = ko.observable(false);
  self.isSendingMessageWithAnnotations = ko.observable(false);
  self.contactSiteError = ko.observable();
  self.totalAnnotations = ko.observable(0);
  self.isSitesFetched = ko.observable(false);
  self.phoneEmailText = ko.observable();
  self.contactedSite = ko.observable(contactedSite);
  self.showRecipientEmailMessage = ko.observable(false);
  self.volunteerLink = ko.observable();
  self.acceptedPrivacyTerms = ko.observable(false);
  self.hasWorkspace = ko.observable(hasWorkspace);
  self.hasUserConsent = ko.observable(false);
  self.isSiteContacted = ko.observable(false);
  self.successMessages = ko.observable(successMessages);
  self.otherLanguage = ko.observable();
  self.is_otherLanguage = ko.observable();
  self.preferredLanguage = ko.observable();
  self.countryDialCode = ko.observable(getDefaultUserLanguage());

  self.handleSiteConnect = handleSiteConnect.bind(self);
  self.mapTrialSite = mapTrialSite.bind(self);
  self.handleTrialSitesChange = handleTrialSitesChange.bind(self);
  self.handleWorkspaceRefreshSuccess = handleWorkspaceRefreshSuccess.bind(self);
  self.handleContactSiteError = handleContactSiteError.bind(self);
  self.resetContactForm = resetContactForm.bind(self);
  self.showContactSiteTab = showContactSiteTab.bind(self);
  self.enableContactSiteTab = enableContactSiteTab.bind(self);
  self.getDialingCode = getDialingCode.bind(self);
  self.sendMessage = sendMessage.bind(self);
  self.sendMessageWithAnnotations = sendMessageWithAnnotations.bind(self);
  self.validateContactForm = validateContactForm.bind(self);
  self.checkPhoneRealWorldValidity = checkPhoneRealWorldValidity.bind(self);
  self.checkIfTrialSiteHasRV = checkIfTrialSiteHasRV.bind(self)
  self.handleOtherLanguage = handleOtherLanguage.bind(self);
  self.setTotalAnnotations = setTotalAnnotations.bind(self);
  self.isContactSiteTabDisabled = isContactSiteTabDisabled.bind(self);
  self.updateContactSiteForm = updateContactSiteForm.bind(self);
  self.handleSitePhoneClick = handleSitePhoneClick.bind(self);
  self.handleSiteEmailClick = handleSiteEmailClick.bind(self);
  self.getContactedSiteMessage = getContactedSiteMessage.bind(self);
  self.setVolunteerLink = setVolunteerLink.bind(self);
  self.handleSignupVolunteer = handleSignupVolunteer.bind(self);
  self.handleConsentChange = handleConsentChange.bind(self);
  self.onResetSiteContacted = onResetSiteContacted.bind(self);
  self.resetContactedSite = resetContactedSite.bind(self);
  self.setRecaptchaSize = setRecaptchaSize.bind(self);
  self.handleCountryDialCodeChange = handleCountryDialCodeChange.bind(self);

  self.contactedSiteMessage = ko.computed(function () {
    return self.getContactedSiteMessage();
  });

  self.errors = ko.computed(function () {
    let errors = self.validateContactForm();

    const isEmailValidatedError = self.isEmailValidatedError()
    if (isEmailValidatedError === true){
      if (errors){
        if (errors.phone === undefined){

        }else{
            errors.phone = 'Please enter a valid and verifiable phone number'
        }
      }else{
        errors = {}
        errors.phone = 'Please enter a valid and verifiable phone number'
      }
    }
    const isSubmitted = self.isSubmitted();

    // Refresh captcha error
    const captchaError = self.captchaError();

    self.isEmailValidatedError(false)

    return isSubmitted && errors ? errors : {};
  });

  functionalConsentChange$.subscribe(self.handleConsentChange);
  ContactSiteApi.getPreferredLanguages()
    .then((languages) => {
      $('#preferred_lang_study')
        .html(
          languages.map(
            ({ display_label, code }) =>
              `<option value="${code}">${display_label}</option>`,
          ),
        )
        .selectpicker('refresh');
    })
    .catch((error) => {
      console.log('preferred_lang_study>>', error);
    });
  $('#preferred_lang_study').on(
    'changed.bs.select',
    function (e, clickedIndex, isSelected, previousValue) {
      var pref_lan = $('#preferred_lang_study').selectpicker('val');
      self.handleOtherLanguage(pref_lan);
    },
  );

  this.$phoneInput.addEventListener(
    'countrychange',
    this.handleCountryDialCodeChange,
  );

  /**
   * Sets the recaptcha size to either `normal` or `compact`,
   * make sure the reCaptcha dependency is included after the current
   * module dependency in order to reflect the size, before the
   * widget gets initialized.
   */
  this.setRecaptchaSize();
}

export default ContactSiteViewModel;
